import { useSelector } from 'react-redux'
import { DownloadOrderConfirmationButton, ReorderButton } from '@Orders/buttons'
import {
    PROVISIONED_UI_FLAGS,
    isOrderTypeIm,
} from '@Orders/OrderDetailsPage/logic'
import { ORDER_CONFIRMATION } from '@Orders/constants'
import { salesOrder_salesOrder as SalesOrderType } from '@Orders/OrderDetailsPage/Queries/__generated__/salesOrder'

import Box from '~/core/Box'
import MoveQuoteOrder from '~/apps/consumer/components/MoveQuoteOrder'
import RemoveFromTeam from '~/apps/consumer/components/RemoveFromTeam'
import useIsAdminPage from '~/lib/useIsAdminPage'
import { selectFlags } from '../selectors'
import DownloadCpqQuoteButton from '../../buttons/DownloadCpqQuoteButton'
import { useFlagEnabled } from '~/lib/isFlagEnabled'

import ReleaseDfmButton from './ReleaseDfmButton'
import { InspectionReportDataBtn } from './InspectionReportData'
import { PartsAndDrawings } from './PartsAndDrawings'

type OrderDetailHeaderPropsType = {
    data: {
        salesOrder: SalesOrderType
    }
    placement: string
    userId?: number
    isObo: boolean
}

const OrderDetailHeader = (props: OrderDetailHeaderPropsType) => {
    const { data, placement, userId, isObo } = props
    const { isTransparencyImprovementEnabled } = useSelector(selectFlags)
    const isLocatedInAdminPage = useIsAdminPage()
    const {
        drawingAttachmentsCount,
        id,
        name,
        salesOrderLines: {
            totalCount: partFilesCount,
            items: salesOrderLineItems,
        },
        shipments,
        sourceQuoteType,
        quoteId,
        team,
        quoteOwnerUser,
        paymentDetails,
        fictivOrderModifiedPostSale,
        __typename,
        quotePdfUrl,
    } = data.salesOrder

    const isImOrder = isOrderTypeIm(sourceQuoteType)
    const isNonImDfmEnabled = useFlagEnabled(PROVISIONED_UI_FLAGS.NON_IM_DFM_UI)
    const isHighTouch = isNonImDfmEnabled && quotePdfUrl // QuotePdfURL is available only for HT Orders. Hence using the condition.
    const shouldShowReleaseDfmButton =
        (isImOrder && isLocatedInAdminPage) || (isHighTouch && isObo)

    const isCpqFlagV2Enabled = useFlagEnabled(
        PROVISIONED_UI_FLAGS.UI_CPQ_QUOTES_INTEGRATION_V2,
    )
    let imPartFilesCount = 0

    if (isImOrder) {
        const imParts = salesOrderLineItems.filter(
            item => item.processName === 'Injection Molding',
        )

        imPartFilesCount = imParts.length
    }

    return (
        <Box width="40%" justifyContent="end">
            {shouldShowReleaseDfmButton && (
                <div>
                    <ReleaseDfmButton
                        data={salesOrderLineItems}
                        isImOrder={isImOrder}
                        orderId={id}
                        quoteId={quoteId}
                        isOnOrderDetailsPage={true}
                    />
                </div>
            )}
            <MoveQuoteOrder
                id={id}
                typeName={__typename}
                name={name}
                showDivider={false}
                placement={placement}
                type="button"
                style={{ marginRight: '8px' }}
                userId={userId}
                teamId={team?.id}
                ownerId={quoteOwnerUser?.id}
                purchaserId={paymentDetails?.paidBy?.id}
            />
            <RemoveFromTeam
                teamId={team?.id}
                teamName={team?.name}
                order={data.salesOrder}
                placement={placement}
                type="button"
                showDivider={false}
                style={{ marginRight: '8px' }}
                userId={userId}
                ownerId={quoteOwnerUser?.id}
                purchaserId={paymentDetails?.paidBy?.id}
            />
            <ReorderButton
                name={name}
                userId={userId}
                teamId={team?.id}
                placement={placement}
            />
            {!isTransparencyImprovementEnabled && (
                <InspectionReportDataBtn
                    id={id}
                    shipments={shipments?.items}
                    placement={placement}
                />
            )}
            <PartsAndDrawings
                name={name}
                drawingAttachmentsCount={drawingAttachmentsCount}
                partFilesCount={isImOrder ? imPartFilesCount : partFilesCount}
                placement={placement}
                salesOrderLineItems={salesOrderLineItems}
                orderId={data?.salesOrder?.id}
                isCpqFlagV2Enabled={isCpqFlagV2Enabled}
            />
            {isCpqFlagV2Enabled && data.salesOrder?.quotePdfUrl ? (
                <DownloadCpqQuoteButton
                    style={{ marginLeft: '10px' }}
                    onODP={true}
                    quotePdfUrl={data.salesOrder?.quotePdfUrl}
                />
            ) : (
                !fictivOrderModifiedPostSale && (
                    <DownloadOrderConfirmationButton
                        buttonLabel={ORDER_CONFIRMATION}
                        name={name}
                        placement={placement}
                    />
                )
            )}
        </Box>
    )
}

export default OrderDetailHeader
